import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'react-hot-toast';
import {
  Users,
  Utensils,
  AlertCircle,
  Check,
  X,
  Info,
  Combine,
} from 'lucide-react';

// Types
interface TableInfo {
  id: string;
  name: string;
  seats: number;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
}

interface MergePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTables: TableInfo[];
  onConfirm: (groupName: string, color?: string) => void;
  onCancel: () => void;
}

interface ValidationError {
  field: string;
  message: string;
}

/**
 * MergePreviewDialog Component
 * 
 * Production-grade dialog for previewing table merge before confirmation.
 * Features:
 * - Input validation with real-time feedback
 * - Accessibility (ARIA labels, keyboard navigation)
 * - Responsive design
 * - Error handling with user-friendly messages
 * - Automatic seats calculation
 * - Color picker for merge group identification
 * 
 * @example
 * <MergePreviewDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   selectedTables={tables}
 *   onConfirm={(name, color) => handleMerge(name, color)}
 *   onCancel={() => setIsOpen(false)}
 * />
 */
export const MergePreviewDialog: React.FC<MergePreviewDialogProps> = ({
  open,
  onOpenChange,
  selectedTables,
  onConfirm,
  onCancel,
}) => {
  // State
  const [groupName, setGroupName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3B82F6'); // Default blue
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Ref
  const inputRef = useRef<HTMLInputElement>(null);

  // Predefined color palette
  const colorPalette = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' },
  ];

  // Calculate total seats
  const totalCapacity = selectedTables.reduce((sum, table) => sum + table.seats, 0);

  // Auto-focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Delay to ensure dialog animation completes
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setGroupName('');
      setSelectedColor('#3B82F6');
      setErrors([]);
      setIsSubmitting(false);
    }
  }, [open]);

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    // Group name validation
    if (!groupName.trim()) {
      newErrors.push({
        field: 'groupName',
        message: 'Group name is required',
      });
    } else if (groupName.trim().length < 2) {
      newErrors.push({
        field: 'groupName',
        message: 'Group name must be at least 2 characters',
      });
    } else if (groupName.trim().length > 50) {
      newErrors.push({
        field: 'groupName',
        message: 'Group name must not exceed 50 characters',
      });
    }

    // Table selection validation
    if (selectedTables.length < 2) {
      newErrors.push({
        field: 'tables',
        message: 'At least 2 tables are required for merging',
      });
    }

    // Check for occupied tables
    const occupiedTables = selectedTables.filter(
      (table) => table.status === 'occupied' || table.status === 'reserved'
    );
    if (occupiedTables.length > 0) {
      newErrors.push({
        field: 'tables',
        message: `Cannot merge occupied/reserved tables: ${occupiedTables.map(t => t.name).join(', ')}`,
      });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before proceeding');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate async operation (in real app, might call API)
      await new Promise((resolve) => setTimeout(resolve, 500));

      onConfirm(groupName.trim(), selectedColor);

      toast.success(`"${groupName.trim()}" created with ${selectedTables.length} tables (${totalCapacity} seats)`);

      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create merge group');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setGroupName('');
    setErrors([]);
    onCancel();
    onOpenChange(false);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Get error for specific field
  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] rounded-md sm:w-full sm:max-w-[500px] md:max-w-[600px] max-h-[85vh] grid grid-rows-[auto_1fr_auto] bg-slate-50 dark:bg-slate-800"
        onKeyDown={handleKeyDown}
        aria-labelledby="merge-preview-title"
        aria-describedby="merge-preview-description"
      >
        <DialogHeader>
          <DialogTitle id="merge-preview-title" className="flex items-center gap-2">
            <Combine className="h-5 w-5 text-primary" />
            Merge Tables Preview
          </DialogTitle>
          <DialogDescription id="merge-preview-description">
            Review and confirm the table merge configuration before proceeding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto px-1">
          {/* Error Alert */}
          {errors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md flex gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                  {errors.map((error) => (
                    <li key={`${error.field}-${error.message}`}>{error.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Group Name Input */}
          <div className="space-y-2">
            <Label htmlFor="group-name" className="text-base font-semibold">
              Group Name *
            </Label>
            <Input
              id="group-name"
              ref={inputRef}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g., Party Section A, VIP Lounge"
              className={getFieldError('groupName') ? 'border-red-500' : ''}
              aria-invalid={!!getFieldError('groupName')}
              aria-describedby={getFieldError('groupName') ? 'group-name-error' : undefined}
              maxLength={50}
            />
            {getFieldError('groupName') && (
              <p id="group-name-error" className="text-sm text-red-500">
                {getFieldError('groupName')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {groupName.length}/50 characters
            </p>
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Group Color</Label>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
              {colorPalette.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSelectedColor(color.value)}
                  className={`
                    h-10 w-full rounded-md border-2 transition-all
                    hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
                    ${selectedColor === color.value ? 'border-primary ring-2 ring-primary' : 'border-gray-300'}
                  `}
                  style={{ backgroundColor: color.value }}
                  aria-label={`Select ${color.name} color`}
                  title={color.name}
                >
                  {selectedColor === color.value && (
                    <Check className="h-5 w-5 text-white mx-auto drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Tables Summary */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Selected Tables ({selectedTables.length})</Label>
            <div className="border rounded-lg p-4 space-y-3 max-h-[200px] overflow-y-auto">
              {selectedTables.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No tables selected</p>
                </div>
              ) : (
                selectedTables.map((table) => (
                  <div
                    key={table.id}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <Utensils className="h-4 w-4 text-primary" />
                      <div>
                        <p className="font-medium">{table.name}</p>
                        <p className="text-xs text-muted-foreground">ID: {table.id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        <Users className="h-3 w-3" />
                        {table.seats}
                      </Badge>
                      <Badge
                        variant={table.status === 'available' ? 'default' : 'destructive'}
                      >
                        {table.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Seats Summary */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-semibold">Total Seats</span>
              </div>
              <div className="text-2xl font-bold text-primary">{totalCapacity}</div>
            </div>
          </div>

          {/* Info Alert */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              Merging tables will create a single unit that can be assigned to reservations.
              You can unmerge tables at any time.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t bg-slate-50 dark:bg-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedTables.length < 2}
            className="gap-2 w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Confirm Merge
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergePreviewDialog;
